export interface SyllabusCatalogSubject {
  code: 'COMBINED_MATHS' | 'PHYSICS' | 'CHEMISTRY' | 'BIOLOGY' | 'ICT';
  name: string;
  colorToken: 'gold' | 'indigo' | 'mint' | 'rose' | 'cyan';
  units: readonly string[];
}

export const SYLLABUS_UNIT_PROGRESS_WEIGHTS: Readonly<Record<string, Readonly<Record<string, number>>>> = {
  COMBINED_MATHS: {
    'Real Numbers and Inequalities': 1,
    'Polynomials and Rational Functions': 1,
    'Mathematical Induction, Series and Progressions': 2,
    'Permutations, Combinations and Binomial Theorem': 2,
    'Matrices and Determinants': 1,
    'Complex Numbers': 1,
    Trigonometry: 1,
    'Limits and Continuity': 1,
    'Differential Calculus': 3,
    'Integral Calculus': 3,
    'Straight Line and Circle (Coordinate Geometry)': 2,
    Vectors: 2,
    'Kinematics (Motion along Line and Plane)': 2,
    'Dynamics and Newton’s Laws of Motion': 3,
    'Work, Power and Energy': 2,
    'Impulse and Direct Impact': 1,
    Statics: 2,
    'Probability and Statistics': 2,
  },
  PHYSICS: {
    Measurement: 1,
    Mechanics: 5,
    'Oscillations and Waves': 3,
    'Thermal Physics': 2,
    'Gravitational Field': 2,
    'Electrostatic Field': 2,
    'Magnetic Field': 2,
    'Current Electricity': 3,
    Electronics: 1,
    'Mechanical Properties of Matter': 2,
    'Matter and Radiation': 2,
  },
  CHEMISTRY: {
    'Atomic Structure': 1,
    'Structure and Bonding': 2,
    'Chemical Calculations': 2,
    'Gaseous State of Matter': 1,
    Energetics: 2,
    'Chemistry of s, p, and d Block Elements': 3,
    'Basic Concepts of Organic Chemistry': 2,
    'Hydrocarbons and Halohydrocarbons': 2,
    'Oxygen Containing Organic Compounds': 3,
    'Nitrogen Containing Organic Compounds': 2,
    'Chemical Kinetics': 2,
    Equilibrium: 2,
    Electrochemistry: 2,
    'Industrial Chemistry and Environmental Pollution': 2,
  },
  BIOLOGY: {
    'Introduction to Biology': 1,
    'Chemical and Cellular Basis of Life': 3,
    'Evolution and Diversity of Organisms': 3,
    'Plant Form and Function': 3,
    'Animal Form and Function': 4,
    Genetics: 2,
    'Molecular Biology and Recombinant DNA Technology': 3,
    'Environmental Biology': 2,
    Microbiology: 2,
    'Applied Biology': 2,
  },
  ICT: {
    'Basic Concepts of ICT': 1,
    'Introduction to Computer Architecture': 2,
    'Data Representation': 2,
    'Fundamentals of Digital Circuits': 2,
    'Computer Operating Systems': 2,
    'Data Communication and Networking': 2,
    'System Analysis and Design': 2,
    'Database Management': 2,
    Programming: 3,
    'Web Development': 2,
    'Internet of Things (IoT)': 2,
    'ICT in Business': 1,
    'New Trends and Future Directions of ICT': 1,
    'Software Project': 3,
  },
};

export const SYLLABUS_CATALOG: readonly SyllabusCatalogSubject[] = [
  {
    code: 'COMBINED_MATHS',
    name: 'Combined Mathematics',
    colorToken: 'gold',
    units: [
      'Real Numbers and Inequalities',
      'Polynomials and Rational Functions',
      'Mathematical Induction, Series and Progressions',
      'Permutations, Combinations and Binomial Theorem',
      'Matrices and Determinants',
      'Complex Numbers',
      'Trigonometry',
      'Limits and Continuity',
      'Differential Calculus',
      'Integral Calculus',
      'Straight Line and Circle (Coordinate Geometry)',
      'Vectors',
      'Kinematics (Motion along Line and Plane)',
      'Dynamics and Newton’s Laws of Motion',
      'Work, Power and Energy',
      'Impulse and Direct Impact',
      'Statics',
      'Probability and Statistics',
    ],
  },
  {
    code: 'PHYSICS',
    name: 'Physics',
    colorToken: 'indigo',
    units: [
      'Measurement',
      'Mechanics',
      'Oscillations and Waves',
      'Thermal Physics',
      'Gravitational Field',
      'Electrostatic Field',
      'Magnetic Field',
      'Current Electricity',
      'Electronics',
      'Mechanical Properties of Matter',
      'Matter and Radiation',
    ],
  },
  {
    code: 'CHEMISTRY',
    name: 'Chemistry',
    colorToken: 'mint',
    units: [
      'Atomic Structure',
      'Structure and Bonding',
      'Chemical Calculations',
      'Gaseous State of Matter',
      'Energetics',
      'Chemistry of s, p, and d Block Elements',
      'Basic Concepts of Organic Chemistry',
      'Hydrocarbons and Halohydrocarbons',
      'Oxygen Containing Organic Compounds',
      'Nitrogen Containing Organic Compounds',
      'Chemical Kinetics',
      'Equilibrium',
      'Electrochemistry',
      'Industrial Chemistry and Environmental Pollution',
    ],
  },
  {
    code: 'BIOLOGY',
    name: 'Biology',
    colorToken: 'rose',
    units: [
      'Introduction to Biology',
      'Chemical and Cellular Basis of Life',
      'Evolution and Diversity of Organisms',
      'Plant Form and Function',
      'Animal Form and Function',
      'Genetics',
      'Molecular Biology and Recombinant DNA Technology',
      'Environmental Biology',
      'Microbiology',
      'Applied Biology',
    ],
  },
  {
    code: 'ICT',
    name: 'Information and Communication Technology (ICT)',
    colorToken: 'cyan',
    units: [
      'Basic Concepts of ICT',
      'Introduction to Computer Architecture',
      'Data Representation',
      'Fundamentals of Digital Circuits',
      'Computer Operating Systems',
      'Data Communication and Networking',
      'System Analysis and Design',
      'Database Management',
      'Programming',
      'Web Development',
      'Internet of Things (IoT)',
      'ICT in Business',
      'New Trends and Future Directions of ICT',
      'Software Project',
    ],
  },
];

export function normalizeSyllabusTitle(title: string): string {
  return title.normalize('NFKD').toLocaleLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
}

export function getSyllabusUnitProgressWeight(subjectCode: string, unitTitle: string): number {
  const weights = SYLLABUS_UNIT_PROGRESS_WEIGHTS[subjectCode];
  if (!weights) return 1;
  const normalizedTitle = normalizeSyllabusTitle(unitTitle);
  const matchingTitle = Object.keys(weights).find((title) => normalizeSyllabusTitle(title) === normalizedTitle);
  return matchingTitle ? weights[matchingTitle] : 1;
}
